import React, { useEffect, useMemo, useState } from 'react';
import { Modal, UnstyledButton, Loader } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { PrinterView } from 'shared-models/browser';
import { Button } from '../../../../components/Button';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import { printersService } from '../../../../services/printers/printers.service';
import classes from './fixed-printer-modal.module.scss';

export type FixedPrinterModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string;
  fixedPrinterId: number | null;
  onSelect: (printerId: number) => void;
  onClear: () => void;
};

// Summary: Manager picker fixing this device's receipt printer (billable printers only).
const FixedPrinterModalComponent: React.FC<FixedPrinterModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
  fixedPrinterId,
  onSelect,
  onClear,
}) => {
  const [printers, setPrinters] = useState<PrinterView[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(fixedPrinterId);

  useEffect(() => {
    if (!opened || !employeeSession) return;
    setSelectedId(fixedPrinterId);
    setLoading(true);
    printersService
      .listActive(tenantId, employeeSession)
      .then((data) => setPrinters(data))
      .catch(() => setPrinters([]))
      .finally(() => setLoading(false));
  }, [opened, tenantId, employeeSession, fixedPrinterId]);

  const billablePrinters = useMemo(() => printers.filter((p) => p.billable), [printers]);

  const handleSave = () => {
    if (selectedId == null) return;
    onSelect(selectedId);
    onClose();
  };

  const handleClear = () => {
    onClear();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      onContextMenu={(event) => event.preventDefault()}
      title={<strong>קיבוע מדפסת למכשיר</strong>}
      centered
      radius="md"
      dir="rtl"
      classNames={{ root: navyModal.root }}
      zIndex={400}
      closeButtonProps={{ 'data-testid': 'fixed-printer-close' } as Record<string, string>}
    >
      <div className={classes.stack}>
        {loading ? (
          <div className={classes.center}><Loader /></div>
        ) : billablePrinters.length === 0 ? (
          <span className={classes.empty}>אין מדפסת חשבונות פעילה</span>
        ) : (
          <div className={classes.list}>
            {billablePrinters.map((printer) => {
              const isSelected = selectedId === printer.id;
              return (
                <UnstyledButton
                  key={printer.id}
                  component="button"
                  type="button"
                  data-testid={`fixed-printer-option-${printer.id}`}
                  className={isSelected ? `${classes.item} ${classes.itemActive}` : classes.item}
                  onClick={() => setSelectedId(printer.id)}
                >
                  <span>{printer.name}</span>
                  {isSelected && <FontAwesomeIcon icon={faCheck as IconProp} />}
                </UnstyledButton>
              );
            })}
          </div>
        )}
        <div className={classes.actions}>
          <Button variant="cancel" onClick={handleClear} disabled={fixedPrinterId == null} dataTestId="fixed-printer-clear">
            נקה / בטל קיבוע
          </Button>
          <Button variant="save" onClick={handleSave} disabled={selectedId == null} dataTestId="fixed-printer-save">
            שמור
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const FixedPrinterModal = React.memo(FixedPrinterModalComponent);
